import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { Plus, FileText, Users, DollarSign, Phone, BookOpen, Archive, Upload, File, Star, Pencil, Check, X } from 'lucide-react';
import AssignTeamMembers from '../../components/Cases/AssignTeamMembers';
import EvidenceTab from '../../components/Cases/EvidenceTab';
import NarrativeTab from '../../components/Cases/NarrativeTab';
import SuspectTab from '../../components/Cases/SuspectTab';
import ChargebackTab from '../../components/Cases/ChargebackTab';
import CommunicationsTab from '../../components/Cases/CommunicationsTab';

const CaseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('details');
  const [todos, setTodos] = useState([]);
  const [showCreateTodo, setShowCreateTodo] = useState(false);
  const [showAssignTeam, setShowAssignTeam] = useState(false);
  const [isEditingStatus, setIsEditingStatus] = useState(false);
  const [isEditingPriority, setIsEditingPriority] = useState(false);
  const [investigators, setInvestigators] = useState([]);
  const [newTodo, setNewTodo] = useState({
    title: '',
    description: '',
    dueDate: '',
    priority: 'Medium',
    assignedTo: ''
  });
  const [extraNotes, setExtraNotes] = useState('');
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [savingTitle, setSavingTitle] = useState(false);

  const fetchCase = useCallback(async () => {
    try {
      const response = await api.get(`/cases/${id}`);
      setCaseData(response.data.data);
    } catch (error) {
      toast.error('Failed to load case');
      navigate('/cases');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchTodos = useCallback(async () => {
    try {
      const response = await api.get('/todos', {
        params: { caseId: id }
      });
      setTodos(response.data.data || []);
    } catch (error) {
      console.error('Error fetching todos:', error);
    }
  }, [id]);

  useEffect(() => {
    fetchCase();
  }, [fetchCase]);

  useEffect(() => {
    fetchInvestigators();
  }, []);

  useEffect(() => {
    if (activeTab === 'details' && caseData) {
      fetchTodos();
      setExtraNotes(caseData.extraNotes || '');
    }
  }, [activeTab, caseData, fetchTodos]);

  const fetchInvestigators = async () => {
    try {
      const response = await api.get('/users/investigators');
      setInvestigators(response.data.data || []);
    } catch (error) {
      console.error('Error fetching investigators:', error);
    }
  };

  const createTodo = async () => {
    try {
      if (!newTodo.title || !newTodo.dueDate) {
        toast.error('Title and due date are required');
        return;
      }
      if (!caseData || !caseData.assignedTo) {
        toast.error('Case data not loaded');
        return;
      }
      
      // Use selected assignedTo or default to case assignedTo
      const assignedToId = newTodo.assignedTo || 
        (caseData.assignedTo?.id || caseData.assignedTo?._id || caseData.assignedTo);
      
      await api.post('/todos', {
        title: newTodo.title,
        description: newTodo.description || undefined,
        dueDate: newTodo.dueDate,
        priority: newTodo.priority,
        caseId: id,
        assignedTo: assignedToId
      });
      toast.success('To-Do created successfully');
      setShowCreateTodo(false);
      setNewTodo({ title: '', description: '', dueDate: '', priority: 'Medium', assignedTo: '' });
      fetchTodos();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create todo');
    }
  };

  const archiveCase = async () => {
    if (!window.confirm('Are you sure you want to archive this case? This action can be undone later.')) {
      return;
    }
    try {
      await api.delete(`/cases/${id}`);
      toast.success('Case archived successfully');
      navigate('/cases');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to archive case');
    }
  };

  const saveCaseTitle = async () => {
    const newTitle = titleDraft.trim();
    if (!newTitle) {
      toast.error('Case title cannot be empty');
      return;
    }
    if (newTitle === caseData.title) {
      setIsEditingTitle(false);
      return;
    }
    try {
      setSavingTitle(true);
      await api.put(`/cases/${id}`, { title: newTitle });
      toast.success('Case renamed successfully');
      setIsEditingTitle(false);
      fetchCase();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to rename case');
    } finally {
      setSavingTitle(false);
    }
  };

  const updateCaseStatus = async (newStatus) => {
    try {
      await api.put(`/cases/${id}`, { status: newStatus });
      toast.success('Case status updated successfully');
      setIsEditingStatus(false);
      fetchCase();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update case status');
    }
  };

  const updateCasePriority = async (newPriority) => {
    try {
      await api.put(`/cases/${id}`, { priority: newPriority });
      toast.success('Case priority updated successfully');
      setIsEditingPriority(false);
      fetchCase();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update case priority');
    }
  };

  const toggleHighProfile = async () => {
    try {
      await api.put(`/cases/${id}`, {
        isHighProfile: !caseData.isHighProfile
      });
      toast.success(caseData.isHighProfile 
        ? 'Case removed from high-profile' 
        : 'Case marked as high-profile');
      fetchCase();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update high-profile status');
    }
  };

  const saveExtraNotes = async () => {
    try {
      setSavingNotes(true);
      await api.put(`/cases/${id}`, {
        extraNotes: extraNotes
      });
      toast.success('Extra notes saved successfully');
      setIsEditingNotes(false);
      fetchCase();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save extra notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const canEditNotes = user?.role === 'Administrator' || 
                      user?.role === 'Supervisor' || 
                      (user?.role === 'Investigator' && caseData?.assignedTo?._id === user._id);

  const tabs = [
    { id: 'details', label: 'Case Details', icon: FileText },
    { id: 'evidence', label: 'Evidence', icon: FileText },
    { id: 'suspect', label: 'Suspect Info', icon: Users },
    { id: 'chargebacks', label: 'Chargebacks', icon: DollarSign },
    { id: 'communications', label: 'Calls & Messages', icon: Phone },
    { id: 'narrative', label: 'Narrative Report', icon: BookOpen },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!caseData) {
    return <div>Case not found</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="min-w-0 flex-1 mr-4">
          {isEditingTitle ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveCaseTitle();
                  if (e.key === 'Escape') setIsEditingTitle(false);
                }}
                autoFocus
                maxLength={255}
                disabled={savingTitle}
                className="text-2xl font-bold text-gray-900 border border-gray-300 rounded-lg px-3 py-1 w-full max-w-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                onClick={saveCaseTitle}
                disabled={savingTitle}
                title="Save"
                className="p-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                <Check className="w-4 h-4" />
              </button>
              <button
                onClick={() => setIsEditingTitle(false)}
                disabled={savingTitle}
                title="Cancel"
                className="p-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 group">
              <h1 className="text-2xl font-bold text-gray-900 truncate">{caseData.title}</h1>
              {!caseData.isArchived && (
                <button
                  onClick={() => {
                    setTitleDraft(caseData.title || '');
                    setIsEditingTitle(true);
                  }}
                  title="Rename case"
                  className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                >
                  <Pencil className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
          <p className="text-gray-600 mt-1">Case #{caseData.caseNumber}</p>
        </div>
        <div className="flex items-center space-x-3">
          {(user?.role === 'Administrator' || user?.role === 'Supervisor') && !caseData.isArchived && (
            <button
              onClick={toggleHighProfile}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                caseData.isHighProfile
                  ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              <Star className={`w-4 h-4 ${caseData.isHighProfile ? 'fill-current' : ''}`} />
              <span>{caseData.isHighProfile ? 'High-Profile' : 'Mark as High-Profile'}</span>
            </button>
          )}
          {user?.role === 'Administrator' && !caseData.isArchived && (
            <button
              onClick={archiveCase}
              className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <Archive className="w-4 h-4" />
              <span>Archive Case</span>
            </button>
          )}
          <button
            onClick={() => navigate('/cases')}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Back to Cases
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6 overflow-x-auto whitespace-nowrap" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'details' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="min-h-[60px]">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                  {isEditingStatus ? (
                    <div className="flex items-center space-x-2">
                      <div className="relative flex-1">
                        <select
                          value={caseData.status}
                          onChange={(e) => updateCaseStatus(e.target.value)}
                          className="w-full px-4 py-2.5 bg-white border-2 border-gray-300 rounded-lg text-sm font-medium text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all appearance-none cursor-pointer hover:border-gray-400 shadow-sm"
                          autoFocus
                          style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
                            backgroundPosition: 'right 0.75rem center',
                            backgroundRepeat: 'no-repeat',
                            backgroundSize: '1.5em 1.5em',
                            paddingRight: '2.5rem'
                          }}
                        >
                          <option value="Open">Open</option>
                          <option value="Initial Review">Initial Review</option>
                          <option value="Ongoing">Ongoing</option>
                          <option value="Closed – No Case">Closed – No Case</option>
                          <option value="Closed – Case Created">Closed – Case Created</option>
                        </select>
                      </div>
                      <button
                        onClick={() => setIsEditingStatus(false)}
                        className="px-3 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-3">
                      <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-semibold ${
                        caseData.status === 'Open' ? 'bg-blue-100 text-blue-800 border border-blue-200' :
                        caseData.status === 'Initial Review' ? 'bg-yellow-100 text-yellow-800 border border-yellow-200' :
                        caseData.status === 'Ongoing' ? 'bg-purple-100 text-purple-800 border border-purple-200' :
                        caseData.status === 'Closed – Case Created' ? 'bg-green-100 text-green-800 border border-green-200' :
                        'bg-gray-100 text-gray-800 border border-gray-200'
                      }`}>
                        {caseData.status}
                      </span>
                      {(user?.role === 'Administrator' || user?.role === 'Supervisor' || 
                        (user?.role === 'Investigator' && caseData.assignedTo?._id === user._id)) && 
                        !caseData.isArchived && (
                        <button
                          onClick={() => setIsEditingStatus(true)}
                          className="px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors border border-blue-200"
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div className="min-h-[60px]">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Priority</label>
                  {isEditingPriority ? (
                    <div className="flex items-center space-x-2">
                      <div className="relative flex-1">
                        <select
                          value={caseData.priority}
                          onChange={(e) => updateCasePriority(e.target.value)}
                          className="w-full px-4 py-2.5 bg-white border-2 border-gray-300 rounded-lg text-sm font-medium text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all appearance-none cursor-pointer hover:border-gray-400 shadow-sm"
                          autoFocus
                          style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
                            backgroundPosition: 'right 0.75rem center',
                            backgroundRepeat: 'no-repeat',
                            backgroundSize: '1.5em 1.5em',
                            paddingRight: '2.5rem'
                          }}
                        >
                          <option value="Urgent">Urgent</option>
                          <option value="High">High</option>
                          <option value="Medium">Medium</option>
                          <option value="Low">Low</option>
                        </select>
                      </div>
                      <button
                        onClick={() => setIsEditingPriority(false)}
                        className="px-3 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-3 flex-wrap gap-2">
                      <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-semibold ${
                        caseData.priority === 'Urgent' ? 'bg-red-100 text-red-800 border border-red-200' :
                        caseData.priority === 'High' ? 'bg-orange-100 text-orange-800 border border-orange-200' :
                        caseData.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800 border border-yellow-200' :
                        'bg-green-100 text-green-800 border border-green-200'
                      }`}>
                        {caseData.priority}
                      </span>
                      {caseData.isHighProfile && (
                        <span className="flex items-center space-x-1 px-2.5 py-1.5 bg-yellow-100 text-yellow-800 rounded-lg text-xs font-semibold border border-yellow-200">
                          <Star className="w-3.5 h-3.5 fill-current" />
                          <span>High-Profile</span>
                        </span>
                      )}
                      {(user?.role === 'Administrator' || user?.role === 'Supervisor' || 
                        (user?.role === 'Investigator' && caseData.assignedTo?._id === user._id)) && 
                        !caseData.isArchived && (
                        <button
                          onClick={() => setIsEditingPriority(true)}
                          className="px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors border border-blue-200"
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Case Type</label>
                  <p className="mt-1 text-sm text-gray-900">{caseData.caseType}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Assigned To</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {caseData.assignedTo?.firstName} {caseData.assignedTo?.lastName}
                  </p>
                </div>
                <div className="col-span-2">
                  <label className="text-sm font-medium text-gray-500">Team Members</label>
                  <div className="mt-1 flex flex-wrap gap-2 mb-2">
                    {caseData.teamMembers && caseData.teamMembers.length > 0 ? (
                      caseData.teamMembers.map((member) => (
                        <span key={member._id} className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                          {member.firstName} {member.lastName}
                        </span>
                      ))
                    ) : (
                      <span className="text-sm text-gray-500">No team members assigned</span>
                    )}
                  </div>
                  {(user?.role === 'Administrator' || user?.role === 'Supervisor' || 
                    (user?.role === 'Investigator' && caseData.assignedTo?._id === user._id)) && (
                    <button
                      onClick={() => setShowAssignTeam(!showAssignTeam)}
                      className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                      {showAssignTeam ? 'Cancel' : 'Assign Team Members'}
                    </button>
                  )}
                  {showAssignTeam && (
                    <AssignTeamMembers 
                      caseId={id} 
                      currentTeamMembers={caseData.teamMembers || []}
                      onSuccess={() => {
                        setShowAssignTeam(false);
                        fetchCase();
                      }}
                    />
                  )}
                </div>
                {caseData.dueDate && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Due Date</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {new Date(caseData.dueDate).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Description</label>
                <p className="mt-1 text-sm text-gray-900">{caseData.description || 'No description'}</p>
              </div>

              {/* Case To-Do's Section */}
              <div className="border-t border-gray-200 pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Case To-Do's</h3>
                  <button
                    onClick={() => setShowCreateTodo(!showCreateTodo)}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add To-Do</span>
                  </button>
                </div>

                {showCreateTodo && (
                  <div className="bg-gray-50 rounded-lg p-4 mb-4 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Title <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={newTodo.title}
                        onChange={(e) => setNewTodo({ ...newTodo, title: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter todo title"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <textarea
                        value={newTodo.description}
                        onChange={(e) => setNewTodo({ ...newTodo, description: e.target.value })}
                        rows={3}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter todo description"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Due Date <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          value={newTodo.dueDate}
                          onChange={(e) => setNewTodo({ ...newTodo, dueDate: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Priority
                        </label>
                        <select
                          value={newTodo.priority}
                          onChange={(e) => setNewTodo({ ...newTodo, priority: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="Urgent">Urgent</option>
                          <option value="High">High</option>
                          <option value="Medium">Medium</option>
                          <option value="Low">Low</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Assign To
                      </label>
                      {user?.role === 'Investigator' ? (
                        <input
                          type="text"
                          value={`${user.firstName} ${user.lastName} (You)`}
                          disabled
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600"
                        />
                      ) : (
                        <select
                          value={newTodo.assignedTo}
                          onChange={(e) => setNewTodo({ ...newTodo, assignedTo: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="">Select investigator (defaults to case assignee)</option>
                          {investigators.map((investigator) => (
                            <option key={investigator.id || investigator._id} value={investigator.id || investigator._id}>
                              {investigator.firstName} {investigator.lastName}
                            </option>
                          ))}
                        </select>
                      )}
                      {!newTodo.assignedTo && caseData?.assignedTo && (
                        <p className="mt-1 text-xs text-gray-500">
                          Default: {caseData.assignedTo.firstName} {caseData.assignedTo.lastName} (Case Assignee)
                        </p>
                      )}
                    </div>
                    <div className="flex items-center justify-end space-x-4">
                      <button
                        onClick={() => {
                          setShowCreateTodo(false);
                          setNewTodo({ title: '', description: '', dueDate: '', priority: 'Medium', assignedTo: '' });
                        }}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={createTodo}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      >
                        Create To-Do
                      </button>
                    </div>
                  </div>
                )}

                {todos.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No to-dos for this case</p>
                ) : (
                  <div className="space-y-3">
                    {todos.map((todo) => (
                      <div
                        key={todo._id}
                        className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900">{todo.title}</h4>
                            {todo.description && (
                              <p className="text-sm text-gray-600 mt-1">{todo.description}</p>
                            )}
                            <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                              <span>Due: {new Date(todo.dueDate).toLocaleDateString()}</span>
                              <span>Priority: {todo.priority}</span>
                              <span>Status: {todo.status}</span>
                            </div>
                          </div>
                          <Link
                            to={`/todos`}
                            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                          >
                            View
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Extra Notes Section */}
              <div className="border-t border-gray-200 pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Extra Notes</h3>
                  {canEditNotes && !caseData.isArchived && !isEditingNotes && (
                    <button
                      onClick={() => setIsEditingNotes(true)}
                      className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Edit
                    </button>
                  )}
                </div>

                {isEditingNotes ? (
                  <div className="space-y-4">
                    <textarea
                      value={extraNotes}
                      onChange={(e) => setExtraNotes(e.target.value)}
                      rows={8}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Enter any additional notes about this case..."
                    />
                    <div className="flex items-center justify-end space-x-4">
                      <button
                        onClick={() => {
                          setIsEditingNotes(false);
                          setExtraNotes(caseData.extraNotes || '');
                        }}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                        disabled={savingNotes}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={saveExtraNotes}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={savingNotes}
                      >
                        {savingNotes ? 'Saving...' : 'Save Notes'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-lg p-4">
                    {extraNotes ? (
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{extraNotes}</p>
                    ) : (
                      <p className="text-sm text-gray-500 italic">No extra notes added yet</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'evidence' && (
            <EvidenceTab caseId={id} assignedTo={caseData.assignedTo} user={user} />
          )}

          {activeTab === 'suspect' && (
            <SuspectTab caseId={id} assignedTo={caseData.assignedTo} user={user} />
          )}

          {activeTab === 'chargebacks' && (
            <ChargebackTab caseId={id} assignedTo={caseData.assignedTo} user={user} />
          )}

          {activeTab === 'communications' && (
            <CommunicationsTab caseId={id} assignedTo={caseData.assignedTo} user={user} />
          )}

          {activeTab === 'narrative' && (
            <NarrativeTab caseId={id} caseData={caseData} user={user} />
          )}
        </div>
      </div>
    </div>
  );
};

export default CaseDetail;
